/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import { Button, typography2 } from '@coursera/cds-core';
import { SuccessIcon } from '@coursera/cds-icons';
import { type EventData, useTracker } from '@coursera/event-pulse/react';

import type {
  Notification as NotificationType,
  SeatExpansionResolvedNotification as SeatExpansionResolvedNotificationType,
} from 'bundles/notification-center/types';

import _t from 'i18n!nls/notification-center';

const styles = {
  container: css`
    ${typography2.bodyPrimary};
    padding: var(--cds-spacing-150) var(--cds-spacing-100);
    word-break: break-word;
    color: var(--cds-color-interactive-secondary);
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  containerRead: css`
    color: var(--cds-color-neutral-primary-weak);
    background-color: var(--cds-color-neutral-background-primary);
  `,
  grid: css`
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: var(--cds-spacing-150);
    align-items: start;
  `,
  iconCell: css`
    display: flex;
    align-items: center;
    align-self: center;
    grid-row: 1;
  `,
  bodyCell: css`
    grid-column: 2;
  `,
  markAsReadButton: css`
    grid-column: 2;
    justify-self: start;
    margin-top: var(--cds-spacing-100);
    padding-left: 0;
  `,
};

type TrackNotificationProps = EventData<'view_notification' | 'click_notification'>;

type SeatExpansionNotificationProps = {
  notification: SeatExpansionResolvedNotificationType;
  rank?: number;
  onClick: (notification: NotificationType) => void;
};

function SeatExpansionResolvedNotification(props: SeatExpansionNotificationProps) {
  const track = useTracker();

  const eventingData: TrackNotificationProps = React.useMemo(
    () => ({
      notification: {
        templateId: props.notification.messageType,
        channelType: 'notification_center' as const,
        id: props.notification.id,
        rank: props.rank,
        isPreviouslyRead: props.notification.isRead,
      },
    }),
    [props.notification.id, props.notification.isRead, props.notification.messageType, props.rank]
  );

  useEffect(() => {
    track('view_notification', eventingData);
  }, [eventingData, props.notification, track]);

  const handleMarkAsRead = () => {
    track('click_notification', eventingData);
    props.onClick(props.notification);
  };

  return (
    <article
      css={[styles.container, props.notification.isRead && styles.containerRead]}
      aria-labelledby={`seat-expansion-notification-title-${props.notification.id}`}
    >
      <div css={styles.grid}>
        <div css={styles.iconCell} aria-hidden="true">
          <SuccessIcon color="success" size="medium" />
        </div>
        <strong id={`seat-expansion-notification-title-${props.notification.id}`}>{_t('You’re In')}</strong>
        <div css={styles.bodyCell}>
          {_t('#{program} was full when you asked to join. Your admin has added a seat for you', {
            program: props.notification.data.programName,
          })}
        </div>
        {!props.notification.isRead && (
          <Button variant="ghost" size="small" onClick={handleMarkAsRead} css={styles.markAsReadButton}>
            {_t('Mark as read')}
          </Button>
        )}
      </div>
    </article>
  );
}

export default SeatExpansionResolvedNotification;
